import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { Loader2, Mic, MicOff, RefreshCw } from "lucide-react";
import Headerdash from "./head";
import Sidebar from "./sidebar";

const messages = {
  verifying: ["Checking your microphone", "Please wait while we confirm your microphone is ready for practice."],
  prompt: ["Enable microphone", "Allow microphone access to unlock your practice question. Choose Allow when your browser asks for permission."],
  requesting: ["Allow microphone access", "Choose Allow in your browser's microphone permission prompt to continue."],
  denied: ["Allow microphone access", "Microphone access is blocked. Open the site permissions beside the address bar and set Microphone to Allow. Also allow microphone access for your browser in your device settings, then retry or reload."],
  missing: ["Microphone not detected", "Connect a microphone or headset and enable it in your device's sound settings. Then check again or reload this page."],
  unavailable: ["Enable your microphone", "Your microphone is unavailable or switched off. Enable it in your device settings and close any app using it, then retry or reload."],
  unsupported: ["Microphone access unavailable", "Open Prepnexis over HTTPS in a browser that supports microphone access, then reload this page."],
};

function MicrophoneCheck() {
  const [status, setStatus] = useState("checking");
  const mounted = useRef(false);
  const pending = useRef(false);
  const permission = useRef(null);

  const checkMicrophone = useCallback(async (silent = false) => {
    if (pending.current) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      setStatus("unsupported");
      return;
    }
    pending.current = true;
    setStatus(silent ? "checking" : "requesting");
    let stream;
    let nextStatus;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const track = stream.getAudioTracks()[0];
      nextStatus = permission.current?.state === "denied" ? "denied" : track && track.readyState === "live" && track.enabled && !track.muted ? "ready" : "unavailable";
    } catch (error) {
      nextStatus = ["NotAllowedError", "SecurityError", "PermissionDeniedError"].includes(error.name)
        ? "denied"
        : ["NotFoundError", "DevicesNotFoundError"].includes(error.name)
        ? "missing"
        : "unavailable";
    } finally {
      // This is only a readiness check; the question's recorder owns recording.
      stream?.getTracks().forEach((track) => track.stop());
    }
    if (!silent && nextStatus === "ready" && mounted.current) {
      setStatus("verifying");
      // Give user-triggered checks a visible transition; automatic checks stay silent.
      await new Promise((resolve) => setTimeout(resolve, 800));
    }
    if (mounted.current) {
      setStatus(permission.current?.state === "denied" ? "denied" : nextStatus);
    }
    pending.current = false;
  }, []);

  useEffect(() => {
    mounted.current = true;
    const initialize = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setStatus("unsupported");
        return;
      }
      try {
        const result = await navigator.permissions?.query({ name: "microphone" });
        if (!mounted.current) return;
        if (result) {
          permission.current = result;
          result.onchange = () => {
            // Enabling access in browser settings must still wait for Check again.
            // Only access that was already granted on arrival is checked silently.
            if (result.state !== "granted") {
              setStatus(result.state === "denied" ? "denied" : "prompt");
            }
          };
          if (result.state === "granted") {
            await checkMicrophone(true);
            return;
          }
          if (result.state === "denied") {
            setStatus("denied");
            return;
          }
        }
      } catch {
        // Some browsers support capture without microphone permission queries.
      }
      if (mounted.current) setStatus("prompt");
    };
    initialize();
    const onDeviceChange = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        if (mounted.current && !devices.some((device) => device.kind === "audioinput")) {
          setStatus("missing");
        }
      } catch {
        // Capture remains the authoritative check when enumeration is restricted.
      }
    };
    navigator.mediaDevices?.addEventListener?.("devicechange", onDeviceChange);
    return () => {
      mounted.current = false;
      if (permission.current) permission.current.onchange = null;
      navigator.mediaDevices?.removeEventListener?.("devicechange", onDeviceChange);
    };
  }, [checkMicrophone]);

  if (status === "ready") return <Outlet />;

  // Keep automatic checks invisible while preventing question timers from starting.
  if (status === "checking") {
    return (
      <div className="min-h-screen bg-slate-50 text-gray-700">
        <Headerdash />
        <Sidebar />
        <main className="min-h-screen pt-24 sm:pl-72" aria-busy="true" aria-label="Loading practice question" />
      </div>
    );
  }

  const busy = status === "verifying" || status === "requesting";
  const [title, description] = messages[status];
  const Icon = busy ? Loader2 : status === "prompt" ? Mic : MicOff;
  return (
    <div className="min-h-screen bg-slate-50 text-gray-700">
      <Headerdash />
      <Sidebar />
      <main className="min-h-screen pt-24 sm:pl-72">
        <div className="mx-auto w-full max-w-7xl px-4 pb-8 sm:px-6 lg:px-8">
          <section className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm">
            <div className="h-1.5 bg-purple-400" />
            <div className="border-b border-slate-100 px-6 py-4">
              <p className="text-sm font-semibold uppercase text-purple-700">Practice · Microphone check</p>
            </div>
            <div className="flex min-h-[420px] flex-col items-center justify-center px-6 py-12 text-center" aria-live="polite" aria-busy={busy}>
              <span className="mb-6 grid h-16 w-16 place-items-center rounded-md bg-purple-50 text-purple-700">
                <Icon className={`h-8 w-8 ${busy ? "animate-spin" : ""}`} aria-hidden="true" />
              </span>
              <h1 className="text-2xl font-extrabold text-gray-900 sm:text-3xl">{title}</h1>
              <p className="mt-3 max-w-xl text-sm leading-6 text-gray-600 sm:text-base">{description}</p>
              <p className="mt-3 text-sm text-gray-500">Your question will start after the microphone check passes.</p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                {status !== "unsupported" && (
                  <button type="button" onClick={() => checkMicrophone()} disabled={busy} className="flex min-h-[44px] items-center gap-2 rounded-md bg-purple-700 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-purple-800 focus:outline-none focus:ring-4 focus:ring-purple-100 disabled:cursor-wait disabled:opacity-60">
                    <Mic className="h-4 w-4" aria-hidden="true" />
                    {busy ? "Checking microphone…" : status === "prompt" ? "Enable microphone" : "Check again"}
                  </button>
                )}
                {!busy && (
                  <button type="button" onClick={() => window.location.reload()} className="flex min-h-[44px] items-center gap-2 rounded-md border border-slate-200 px-5 py-2.5 text-sm font-bold text-gray-700 hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-purple-100">
                    <RefreshCw className="h-4 w-4" aria-hidden="true" />Reload page
                  </button>
                )}
              </div>
              <Link to="/practice" className="mt-6 text-sm font-semibold text-purple-700 hover:underline">Back to practice</Link>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}

export default function PracticeMicrophoneGate() {
  const location = useLocation();
  return <MicrophoneCheck key={location.pathname} />;
}
